<script setup>
/**
 * Teammates.
 *
 * One page for the people in the workspace, Intercom's grammar: Active is the
 * roster of confirmed teammates, Invited holds the outstanding invitations
 * (Chatwoot models an invite as an unconfirmed agent), Roles mounts the
 * custom-roles list and Activity the audit log - each gated by the same flags
 * that guard their standalone routes, which stay reachable for deep links.
 *
 * The tab lives in `?tab=` so every tab is linkable; the Activity tab's
 * pagination stacks its own `page` param on top.
 */
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import CustomRolesList from '../customRoles/CustomRolesList.vue';
import AuditLogsList from '../auditlogs/AuditLogsList.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { shouldShow } = usePolicy();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const activeAgents = computed(() =>
  agentList.value.filter(agent => agent.confirmed)
);
const invitedAgents = computed(() =>
  agentList.value.filter(agent => !agent.confirmed)
);

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// The Roles and Activity tabs mirror the gates on their standalone routes:
// same flags, same admin-only permission, same installation types.
const enterpriseInstallations = [
  INSTALLATION_TYPES.CLOUD,
  INSTALLATION_TYPES.ENTERPRISE,
];
const showRolesTab = computed(() =>
  shouldShow(
    FEATURE_FLAGS.CUSTOM_ROLES,
    ['administrator'],
    enterpriseInstallations
  )
);
const showActivityTab = computed(() =>
  shouldShow(
    FEATURE_FLAGS.AUDIT_LOGS,
    ['administrator'],
    enterpriseInstallations
  )
);

const tabs = computed(() => [
  { key: 'active', label: t('AGENT_MGMT.TABS.ACTIVE') },
  {
    key: 'invited',
    label: t('AGENT_MGMT.TABS.INVITED'),
    count: invitedAgents.value.length,
  },
  ...(showRolesTab.value
    ? [{ key: 'roles', label: t('AGENT_MGMT.TABS.ROLES') }]
    : []),
  ...(showActivityTab.value
    ? [{ key: 'activity', label: t('AGENT_MGMT.TABS.ACTIVITY') }]
    : []),
]);

const activeTab = computed(() => {
  const requested = route.query.tab;
  return tabs.value.some(tab => tab.key === requested) ? requested : 'active';
});
const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);

const onTabChange = tab => {
  if (tab.key === activeTab.value) return;
  // `page` belongs to the Activity tab's pagination; a tab switch resets it.
  const query = { ...route.query };
  delete query.page;
  delete query.tab;
  if (tab.key !== 'active') query.tab = tab.key;
  router.push({ name: route.name, query });
};

const isRosterTab = computed(() =>
  ['active', 'invited'].includes(activeTab.value)
);

const rosterAgents = computed(() =>
  activeTab.value === 'invited' ? invitedAgents.value : activeAgents.value
);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return rosterAgents.value;
  return picoSearch(rosterAgents.value, query, ['name', 'email']);
});

const tableHeaders = computed(() => [
  t('AGENT_MGMT.LIST.NAME'),
  t('AGENT_MGMT.LIST.ROLE'),
  t('AGENT_MGMT.LIST.STATUS'),
  t('AGENT_MGMT.LIST.ACTIONS'),
]);

const noDataMessage = computed(() => {
  if (searchQuery.value) return t('AGENT_MGMT.NO_RESULTS');
  return activeTab.value === 'invited'
    ? t('AGENT_MGMT.INVITED_LIST.404')
    : t('AGENT_MGMT.LIST.404');
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('AGENT_MGMT.HEADER')"
    :description="$t('AGENT_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        v-if="isRosterTab"
        :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
        size="sm"
        @click="openAddPopup"
      />
    </template>

    <template #tabs>
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChange"
      />
    </template>

    <template v-if="isRosterTab">
      <div class="flex items-center justify-between gap-3">
        <SettingsSearchInput
          v-model="searchQuery"
          :placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        />
        <span v-if="rosterAgents.length" class="text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.COUNT', { n: rosterAgents.length }) }}
        </span>
      </div>

      <div
        v-if="uiFlags.isFetching"
        role="status"
        aria-busy="true"
        :aria-label="$t('AGENT_MGMT.LOADING')"
        class="flex flex-col gap-5"
      >
        <Skeleton
          v-for="row in 4"
          :key="row"
          variant="text"
          :lines="2"
          :announce="false"
        />
      </div>

      <BaseTable
        v-else
        :headers="tableHeaders"
        :items="filteredAgentList"
        :no-data-message="noDataMessage"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="agent in items" :key="agent.email" :item="agent">
            <template #default>
              <BaseTableCell>
                <div class="flex items-center gap-3 py-1">
                  <Avatar
                    :src="agent.thumbnail"
                    :name="agent.name"
                    :status="agent.availability_status"
                    :size="32"
                    hide-offline-status
                  />
                  <div class="flex flex-col min-w-0 gap-0.5">
                    <span
                      class="block truncate text-body-main text-n-slate-12 capitalize"
                    >
                      {{ agent.name }}
                    </span>
                    <span class="block truncate text-body-main text-n-slate-11">
                      {{ agent.email }}
                    </span>
                  </div>
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="block w-fit text-body-main text-n-slate-11 relative"
                  :class="{
                    'hover:text-n-slate-12 group cursor-pointer':
                      agent.custom_role_id,
                  }"
                >
                  {{ getAgentRoleName(agent) }}

                  <div
                    class="absolute ltr:left-0 rtl:right-0 z-10 hidden w-[300px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl outline outline-1 outline-n-container shadow-lg top-8"
                    :class="{ 'group-hover:block': agent.custom_role_id }"
                  >
                    <div class="flex flex-col gap-1 p-4">
                      <span class="text-heading-3 text-n-slate-12">
                        {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                      </span>
                      <ul class="ltr:pl-4 rtl:pr-4 mb-0 list-disc">
                        <li
                          v-for="permission in getAgentRolePermissions(agent)"
                          :key="permission"
                          class="text-body-main text-n-slate-11"
                        >
                          {{
                            $t(
                              `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                            )
                          }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{
                    agent.confirmed
                      ? $t('AGENT_MGMT.LIST.VERIFIED')
                      : $t('AGENT_MGMT.LIST.VERIFICATION_PENDING')
                  }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex justify-end gap-3 flex-shrink-0">
                  <Button
                    v-if="showEditAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditPopup(agent)"
                  />
                  <Button
                    v-if="showDeleteAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <CustomRolesList v-else-if="activeTab === 'roles'" />
    <AuditLogsList v-else-if="activeTab === 'activity'" />

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <ConfirmDialog
      v-model:show="showDeletePopup"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :description="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :description-value="deleteMessage"
      :confirm-button-label="deleteConfirmText"
      :cancel-button-label="deleteRejectText"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
